/**
 * The rest of the copy (F14 perf, K17): the first catalogue carries what renders before the sign-in is done
 * (the auth screens, app-core's states, the error boundary); the rest is requested at start-up (main.tsx) and the
 * signed-in app waits for it, so no page ever shows a raw key. Without a split (tests) the rest is empty. Both are
 * the active locale's (L1): a language switch loads the new locale's rest before it changes the language.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { activeLocale, I18nKitCopy } from "@brevalt/app-core";
import { localeRuntime } from "@brevalt/app-core/locale-runtime";

/** Starts loading the rest of `lang`'s copy (idempotent); main.tsx calls it before anything renders. */
export function loadRestCopy(lang = activeLocale()): Promise<Record<string, string>> {
  return localeRuntime.load(lang, "rest");
}

/**
 * Renders `children` once the rest of the copy is in the i18n instance (suspends until then). The kit's words
 * beyond the first screen's are in the rest: a provider of its own reads them in the same render, so the first
 * frame of the shell already has them in the active language (no English flash, REQUESTS #141).
 */
export function RestCopy({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  localeRuntime.usePart(i18n, "rest");
  return <I18nKitCopy>{children}</I18nKitCopy>;
}
